import { useKeycloak } from "@react-keycloak/web"
import React, { createContext } from "react"
import { useIntl } from "react-intl"
import { useHistory } from "react-router-dom"
import SubscriptionServices from "./Services/Consumers/SubscriptionServices"
import { NamedRoutes } from "./routes"
import { read, utils } from 'xlsx'
import moment from 'moment'

const PromocodeContext = createContext({})

const PromocodeProvider = ({ children, promocodeId, isEdit, handleNotificationCall }) => {
  const { keycloak } = useKeycloak()
  const intl = useIntl()
  const history = useHistory()
  const [promocodeData, setPromocodeData] = React.useState({})
  const [isLoading, setIsLoading] = React.useState(false)
  const [promocodeDataFailure, setPromocodeDataFailure] = React.useState(false)
  const [bulkImportHasError, setBulkImportHasError] = React.useState(false)

  React.useEffect(() => {
    if (isEdit && !!promocodeId && !!parseInt(promocodeId)) {
      getPromocodeData()
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  const getPromocodeData = () => {
    if (!!keycloak?.authenticated) {
      setIsLoading(true)
      setPromocodeDataFailure(false)
      SubscriptionServices.getPromocodeData(promocodeId, {
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` }
      }).then((result) => {
        if ((!!result && result.status !== 200) || !result) {
          setPromocodeDataFailure(true)
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            setPromocodeData(result.data.data)
          } else {
            setPromocodeDataFailure(true)
          }
        }
      }).then(() => {
        setIsLoading(false)
      })
    }
  }

  const handleSubmit = (values, FormikActions) => {
    if (!!keycloak?.authenticated) {
      FormikActions.setSubmitting(true)
      SubscriptionServices.submitPromocode(
        promocodeId,
        isEdit,
        values,
        { headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` } },
        null,
        null,
        FormikActions
      ).then((result) => {
        if (result && result.data && result.status === 200) {
          handleNotificationCall({
            type: 'defence',
            message: intl.formatMessage({
              id: isEdit ? 'Promocode.SuccessUpdate' : 'Promocode.SuccessCreate'
            }),
            color: 'success'
          }, 'success')
          history.push(NamedRoutes.subscription.promocodes.list)
        } else {
          let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
          if (result.response && result.response.data) {
            if (result.response.data && result.response.data.status && result.response.data.status.errors) {
              message = result.response.data.status.errors
            }
          }
          handleNotificationCall({
            type: 'defence',
            message: message,
            color: 'error'
          }, 'error')
        }
      })
    }
  }

  const performSearch = async (searchType, searchTerm, params = {}) => {
    if (!keycloak?.authenticated || searchTerm.length < 2) {
      return { success: false, data: [] }
    }

    try {
      let result
      const config = {
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` },
        params: { ...params, name: searchTerm }
      }

      switch (searchType) {
        case 'users':
          result = await SubscriptionServices.searchUsers(config)
          break
        case 'userGroups':
          result = await SubscriptionServices.getUserGroups(config)
          break
        case 'externalEntityGroups':
          result = await SubscriptionServices.getExternalEntityGroups(config)
          break
        default:
          return { success: false, data: [] }
      }

      if (!!result && !!result?.data && result?.data?.status?.code === 200) {
        return { success: true, data: result.data.data || [] }
      }

      return { success: false, data: [], error: result }
    } catch (error) {
      return { success: false, data: [], error }
    }
  }

  const getPromocodes = async (params) => {
    if (!keycloak?.authenticated) {
      return { success: false, data: [], error: 'Not authenticated' }
    }

    try {
      const result = await SubscriptionServices.getPromocodes({
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` },
        params
      })

      if (result && result.data && result.status === 200) {
        return {
          success: true,
          data: result.data.data || [],
          meta: result.data.meta || {}
        }
      }

      return { success: false, data: [], error: result }
    } catch (error) {
      return { success: false, data: [], error }
    }
  }

  const handleBulkImportSubmit = async (values, FormikActions, closeDialog, reloadPage) => {
    if (!!keycloak?.authenticated) {
      try {
        const file = values.promocodesFile

        const fileBuffer = await file.arrayBuffer()
        const workbook = read(fileBuffer)
        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        const data = utils.sheet_to_json(worksheet)

        if (!data.length) {
          handleNotificationCall({
            type: 'defence',
            message: intl.formatMessage({ id: 'PromocodeBulkImport.file.empty' }),
            color: 'error'
          }, 'error')
          setTimeout(() => {
            closeDialog()
          }, 500)
          return
        }

        const payloadRows = data.map(row => ({
          code: row['code'],
          title: row['title'],
          discount_type: row['discount_type'],
          discount_value: row['discount_value'] ? parseInt(row['discount_value']) : null,
          max_discount: row['max_discount'] ? parseInt(row['max_discount']) : null,
          usage_limit: row['usage_limit'] ? parseInt(row['usage_limit']) : null,
          start_date: !isNaN(row['start_date']) ?
            moment("1899-12-30").add(row['start_date'], "days").locale('en-us').format("YYYY-MM-DD")
            : moment(row['start_date'], ["YYYY-MM-DD", "MM/DD/YYYY"], true).isValid() ?
              moment(row['start_date']).locale('en-us').format("YYYY-MM-DD")
              : moment().locale('en-us').format("YYYY-MM-DD"),
          end_date: !isNaN(row['end_date']) ?
            moment("1899-12-30").add(row['end_date'], "days").locale('en-us').format("YYYY-MM-DD")
            : moment(row['end_date'], ["YYYY-MM-DD", "MM/DD/YYYY"], true).isValid() ?
              moment(row['end_date']).locale('en-us').format("YYYY-MM-DD")
              : moment().locale('en-us').format("YYYY-MM-DD"),
          is_active: row['is_active'],
          user_id: row['user_id'] || null,
          user_group_id: row['user_group_id'] || null,
          external_entity_reference: row['external_entity_reference'] || null,
          external_entity_type: row['external_entity_type'] || null,
          external_entity_group_id: row['external_entity_group_id'] || null,
          payable_type: row['payable_type'] || null,
          payable_id: row['payable_id'] || null,
          priceable_type: row['priceable_type'] || null,
          priceable_id: row['priceable_id'] || null,
        }))

        const bulkImportPayload = { data: payloadRows }

        const result = await SubscriptionServices.bulkImportPromocodes(
          bulkImportPayload,
          { headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` } },
          null,
          null,
          FormikActions
        )

        if (result && result.data && result.status === 200) {
          const count = result.data.data?.created || result.data.data?.updated || 0
          setBulkImportHasError(false)
          if (!!count) {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'PromocodeBulkImport.success.message' }, { count }),
              color: 'success'
            }, 'success')
          } else {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'PromocodeBulkImport.noData' }),
              color: 'error'
            }, 'error')
          }
          FormikActions.setSubmitting(false)
          FormikActions.resetForm()
          // Close dialog and reload page
          closeDialog()
          reloadPage(true)
        } else {
          throw new Error(result?.response?.data?.status?.errors || result?.response?.data?.status?.message || intl.formatMessage({ id: 'bulkImport.failed' }))
        }
      } catch (error) {
        setBulkImportHasError(true)
        handleNotificationCall({
          type: 'defence',
          message: error?.message || intl.formatMessage({ id: 'bulkImport.error' }),
          color: 'error'
        }, 'error')
        FormikActions.setSubmitting(false)
        FormikActions.resetForm()
      }
    }
  }

  const clearBulkImportError = () => {
    setBulkImportHasError(false)
  }

  return (
    <PromocodeContext.Provider value={{
      promocodeData,
      promocodeDataFailure,
      isLoading,
      bulkImportHasError,
      isEdit,

      handleSubmit,
      performSearch,
      getPromocodes,
      handleBulkImportSubmit,
      clearBulkImportError
    }}>
      {children}
    </PromocodeContext.Provider>
  )
}

const PromocodeConsumer = PromocodeContext.Consumer

export { PromocodeProvider, PromocodeConsumer }